import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useLang } from "../i18n";
import { Reveal, Eyebrow } from "../components/Reveal";
import EditorialProject from "../components/EditorialProject";

const FILTERS = ["all", "windows", "doors", "facades", "outdoor", "awnings", "railings", "corten"];

export default function Projects() {
  const { t } = useLang();
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState([]);
  const visibleFilters = useMemo(() => FILTERS.filter((f) => f === "all" || available.includes(f)), [available]);

  useEffect(() => {
    api.get("/projects").then(({ data }) => setAvailable([...new Set(data.map((p) => p.category))])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/projects", { params: { category: filter } })
      .then(({ data }) => setProjects(data))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, [filter]);

  const filterLabels = {
    all: t.filter_all,
    windows: t.filter_windows,
    doors: t.filter_doors,
    facades: t.filter_facades,
    outdoor: t.filter_outdoor,
    awnings: t.filter_awnings,
    railings: t.filter_railings,
    corten: t.filter_corten,
  };

  return (
    <div data-testid="projects-page" className="pt-32 sm:pt-40 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>{t.projects_eyebrow}</Eyebrow>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#F3EFE6]">{t.projects_title}</h1>
          <p className="mt-6 max-w-xl text-sm sm:text-base font-light text-[#A39E93] leading-relaxed">{t.projects_sub}</p>
        </Reveal>

        <Reveal delay={0.15} className="mt-12">
          <div className="flex flex-wrap gap-2" data-testid="project-filters">
            {visibleFilters.map((f) => (
              <button
                key={f}
                data-testid={`filter-btn-${f}`}
                onClick={() => setFilter(f)}
                className={`border px-5 py-2 text-xs uppercase tracking-[0.15em] transition-all duration-300 ${
                  filter === f
                    ? "border-[#C8A86B] bg-[#C8A86B] text-[#080808]"
                    : "border-[#262420] text-[#A39E93] hover:border-[#C8A86B]/50 hover:text-[#F3EFE6]"
                }`}
              >
                {filterLabels[f]}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-20">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="aspect-[4/3] animate-pulse border border-[#1C1A17] bg-[#111110]" />
              ))}
            </div>
          ) : projects.length === 0 ? (
            <div data-testid="projects-empty-state" className="border border-dashed border-[#262420] py-24 text-center">
              <p className="font-serif text-2xl text-[#F3EFE6]">{t.empty_title}</p>
              <p className="mt-3 text-sm font-light text-[#A39E93]">{t.empty_sub}</p>
            </div>
          ) : (
            <div data-testid="projects-editorial-list" className="flex flex-col gap-24 sm:gap-36">
              {projects.map((p, i) => (
                <Reveal key={p.id} delay={0.05}>
                  <EditorialProject project={p} index={i} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
